/**
 * Runs in the background and fires browser notifications when alert thresholds
 * are crossed.  Import once at app root (e.g. in TerminalLayout or App).
 */
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAlertStore } from '../store/alertStore'

/** Compute EMA of a series of prices (most recent last). */
function computeEMA(prices: number[], period: number): number | null {
  if (prices.length < period) return null
  const k = 2 / (period + 1)
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period
  for (let i = period; i < prices.length; i++) {
    ema = prices[i] * k + ema * (1 - k)
  }
  return ema
}

function notify(title: string, body: string) {
  if (Notification.permission !== 'granted') return
  try {
    new Notification(title, { body, icon: '/favicon.ico' })
  } catch {
    // Safari may block programmatic Notification construction
  }
}

async function postDiscord(webhookUrl: string, title: string, body: string) {
  if (!webhookUrl) return
  try {
    await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: `**${title}**\n${body}` }),
    })
  } catch {
    // Network error — don't surface to user
  }
}

async function postTelegram(botToken: string, chatId: string, title: string, body: string) {
  if (!botToken || !chatId) return
  try {
    await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        text: `<b>${title}</b>\n${body}`,
        parse_mode: 'HTML',
      }),
    })
  } catch {
    // Network error — don't surface to user
  }
}

async function checkNewsAlert(symbol: string, keyword: string, sinceIso: string): Promise<boolean> {
  try {
    const { api } = await import('../lib/api')
    const res = await api.post<{ found: boolean }>('/market/news-check', { symbol, keyword, since_iso: sinceIso })
    return res.data.found
  } catch {
    return false
  }
}

import type { AlertMetric, AlertCondition, CompoundCondition } from '../store/alertStore'
import type { QueryClient } from '@tanstack/react-query'

function getMetricValue(qc: QueryClient, symbol: string, metric: AlertMetric): number | null {
  if (metric === 'price') {
    return qc.getQueryData<{ price: number }>(['quote', symbol])?.price ?? null
  } else if (metric === 'change_pct') {
    return qc.getQueryData<{ changePercent: number | null }>(['quote', symbol])?.changePercent ?? null
  } else if (metric === 'rsi') {
    return qc.getQueryData<{ rsi: number | null }>(['technicals', symbol])?.rsi ?? null
  } else if (metric === 'macd_hist') {
    return qc.getQueryData<{ macdHist: number | null }>(['technicals', symbol])?.macdHist ?? null
  } else if (metric === 'volume') {
    return qc.getQueryData<{ volume: number }>(['quote', symbol])?.volume ?? null
  } else if (metric === 'rvol') {
    return qc.getQueryData<{ rvol: number | null }>(['quote', symbol])?.rvol ?? null
  }
  return null
}

function evalCondition(value: number, condition: AlertCondition, threshold: number): boolean {
  if (condition === 'above') return value >= threshold
  if (condition === 'below') return value <= threshold
  return false
}

export function useAlertChecker() {
  const { alerts, markTriggered, updateNewsCheck, discordWebhookUrl, telegramBotToken, telegramChatId } = useAlertStore()
  const qc = useQueryClient()

  // News alert polling — every 5 minutes (separate from the 5s price check)
  useEffect(() => {
    const newsAlerts = alerts.filter((a) => a.active && !a.triggered && a.metric === 'news')
    if (!newsAlerts.length) return

    const check = async () => {
      for (const alert of newsAlerts) {
        if (!alert.keyword) continue
        const since = alert.lastNewsCheck ?? alert.createdAt
        const found = await checkNewsAlert(alert.symbol, alert.keyword, since)
        updateNewsCheck(alert.id)
        if (found) {
          markTriggered(alert.id)
          const title = `${alert.symbol} News Alert`
          const body  = `Keyword "${alert.keyword}" detected in recent news`
          notify(title, body)
          postDiscord(discordWebhookUrl, title, body)
          postTelegram(telegramBotToken, telegramChatId, title, body)
        }
      }
    }

    check()
    const newsInterval = setInterval(check, 5 * 60_000)
    return () => clearInterval(newsInterval)
  }, [alerts, markTriggered, updateNewsCheck, discordWebhookUrl, telegramBotToken, telegramChatId])

  useEffect(() => {
    if (!alerts.some((a) => a.active && !a.triggered && a.metric !== 'news')) return

    const interval = setInterval(() => {
      alerts.forEach((alert) => {
        if (alert.metric === 'news') return  // handled separately above
        if (!alert.active || alert.triggered) return

        let currentValue: number | null = null

        if (alert.metric === 'price') {
          const quote = qc.getQueryData<{ price: number }>(['quote', alert.symbol])
          currentValue = quote?.price ?? null
        } else if (alert.metric === 'change_pct') {
          const quote = qc.getQueryData<{ changePercent: number | null }>(['quote', alert.symbol])
          currentValue = quote?.changePercent ?? null
        } else if (alert.metric === 'rsi') {
          const tech = qc.getQueryData<{ rsi: number | null }>(['technicals', alert.symbol])
          currentValue = tech?.rsi ?? null
        } else if (alert.metric === 'macd_hist') {
          const tech = qc.getQueryData<{ macdHist: number | null }>(['technicals', alert.symbol])
          currentValue = tech?.macdHist ?? null
        } else if (alert.metric === 'volume') {
          const quote = qc.getQueryData<{ volume: number }>(['quote', alert.symbol])
          currentValue = quote?.volume ?? null
        } else if (alert.metric === 'rvol') {
          const quote = qc.getQueryData<{ rvol: number | null }>(['quote', alert.symbol])
          currentValue = quote?.rvol ?? null
        } else if (alert.metric === 'ema_cross_golden') {
          const history = qc.getQueryData<Array<{ close: number }>>(['priceHistory', alert.symbol, '1y'])
          if (!history || history.length < 200) return
          const closes = history.map((h) => h.close)
          const ema50  = computeEMA(closes, 50)
          const ema200 = computeEMA(closes, 200)
          if (ema50 === null || ema200 === null) return
          currentValue = ema50 > ema200 ? 1 : 0
        } else if (alert.metric === 'ema_cross_death') {
          const history = qc.getQueryData<Array<{ close: number }>>(['priceHistory', alert.symbol, '1y'])
          if (!history || history.length < 200) return
          const closes = history.map((h) => h.close)
          const ema50  = computeEMA(closes, 50)
          const ema200 = computeEMA(closes, 200)
          if (ema50 === null || ema200 === null) return
          currentValue = ema50 < ema200 ? 1 : 0
        } else if (alert.metric === 'ema_cross') {
          // Use cached price history closes to compute EMA50 and EMA200
          const history = qc.getQueryData<Array<{ close: number }>>(['priceHistory', alert.symbol, '1y'])
          if (!history || history.length < 200) return
          const closes = history.map((h) => h.close)
          const ema50  = computeEMA(closes, 50)
          const ema200 = computeEMA(closes, 200)
          if (ema50 === null || ema200 === null) return
          const triggered =
            (alert.condition === 'golden' && ema50 > ema200) ||
            (alert.condition === 'death'  && ema50 < ema200)
          if (triggered) {
            markTriggered(alert.id)
            const title = `${alert.symbol} EMA Cross Alert`
            const body  = alert.condition === 'golden'
              ? `Golden Cross: 50 EMA (${ema50.toFixed(2)}) crossed above 200 EMA (${ema200.toFixed(2)})`
              : `Death Cross: 50 EMA (${ema50.toFixed(2)}) crossed below 200 EMA (${ema200.toFixed(2)})`
            notify(title, body)
            postDiscord(discordWebhookUrl, title, body)
            postTelegram(telegramBotToken, telegramChatId, title, body)
          }
          return
        } else if (alert.metric === 'week52_pct') {
          const quote = qc.getQueryData<{ price: number; week52High?: number; week52Low?: number }>(['quote', alert.symbol])
          const currentPrice = quote?.price ?? null
          const week52High   = quote?.week52High ?? null
          const week52Low    = quote?.week52Low  ?? null
          if (!currentPrice || !week52High || !week52Low) return
          const pctFromHigh = ((week52High - currentPrice) / week52High) * 100
          const pctFromLow  = ((currentPrice - week52Low)  / week52Low)  * 100
          const triggered =
            (alert.condition === 'near_high' && pctFromHigh <= alert.threshold) ||
            (alert.condition === 'near_low'  && pctFromLow  <= alert.threshold)
          if (triggered) {
            markTriggered(alert.id)
            const title = `${alert.symbol} 52W Proximity Alert`
            const body  = alert.condition === 'near_high'
              ? `Within ${pctFromHigh.toFixed(1)}% of 52W high ($${week52High.toFixed(2)})`
              : `Within ${pctFromLow.toFixed(1)}% of 52W low ($${week52Low.toFixed(2)})`
            notify(title, body)
            postDiscord(discordWebhookUrl, title, body)
            postTelegram(telegramBotToken, telegramChatId, title, body)
          }
          return
        }

        if (currentValue === null) return

        const crossed =
          alert.condition === 'above'
            ? currentValue >= alert.threshold
            : currentValue <= alert.threshold

        if (crossed) {
          markTriggered(alert.id)
          const title = `${alert.symbol} Alert`
          const suffix = alert.metric === 'change_pct' ? '%' : ''
          const body  = `${alert.metric.toUpperCase()} ${alert.condition} ${alert.threshold}${suffix} — current: ${currentValue.toFixed(2)}${suffix}`
          notify(title, body)
          postDiscord(discordWebhookUrl, title, body)
          postTelegram(telegramBotToken, telegramChatId, title, body)
        }
      })

      // ── Compound multi-factor alerts ─────────────────────────────────────
      alerts
        .filter((a) => a.alertType === 'compound' && a.active && !a.triggered && a.compoundConditions?.length)
        .forEach((alert) => {
          const conditions = alert.compoundConditions!
          const results = conditions.map((cond: CompoundCondition) => {
            const val = getMetricValue(qc, alert.symbol, cond.metric)
            if (val === null) return null
            return evalCondition(val, cond.condition, cond.threshold)
          })
          if (results.includes(null)) return  // missing data

          const logic = alert.compoundLogic ?? 'AND'
          const fired = logic === 'AND'
            ? results.every(Boolean)
            : results.some(Boolean)

          if (fired) {
            markTriggered(alert.id)
            const condSummary = conditions.map((c: CompoundCondition) => `${c.metric} ${c.condition} ${c.threshold}`).join(` ${logic} `)
            const title = `${alert.symbol} Multi-Factor Alert`
            const body  = condSummary
            notify(title, body)
            postDiscord(discordWebhookUrl, title, body)
            postTelegram(telegramBotToken, telegramChatId, title, body)
          }
        })
    }, 5_000)

    return () => clearInterval(interval)
  }, [alerts, qc, markTriggered, discordWebhookUrl, telegramBotToken, telegramChatId])
}
